/**
 * @copyright Microsoft Corporation. All rights reserved.
 *
 * @file Analytics Stats component
 */

import * as React from 'react';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { Link } from 'office-ui-fabric-react/lib/Link';
import {
  MalwareDetectedSignal,
  YouCheckedOutSignal,
  SomeoneCheckedOutSignal,
  BlockedSignal,
  MissingMetadataSignal,
  WarningSignal,
  AwaitingApprovalSignal,
  TrendingSignal,
  NewSignal
} from '@uifabric/experiments/lib/components/signals/Signals';
import * as StringHelper from '@ms/utilities-strings/lib/index';
import { ILifeCycleStatus, AnalyticsLifeCycleStatus } from '../../../models/ItemAnalytics';
import strings from '../FileHoverCard.resx';
import { isCurrentLoginUser } from '../UserNameHelper';
import * as AnalyticsLifeCycleStyle from './AnalyticsLifeCycle.scss';

const LEARN_MORE_LINK = `https://go.microsoft.com/fwlink/?linkid=862871`;

export interface IAnalyticsLifeCycleProps {
  lifeCycleStatus: ILifeCycleStatus;
  loginUserEmail?: string;
  userEmail?: string;
  onViewPolicyTip?: () => void;
  onAddMetadataNow?: () => void;
  onCheckIn?: () => void;
  onDiscardCheckOut?: () => void;
  cleanCachedItem?: () => void;
}

export class AnalyticsLifeCycle extends React.Component<IAnalyticsLifeCycleProps, {}> {
  public static defaultProps: IAnalyticsLifeCycleProps = {
    lifeCycleStatus: {},
    onViewPolicyTip: () => { console.log('onViewPolicyTip'); },
    onAddMetadataNow: () => { console.log('onAddMetadataNow'); },
    onCheckIn: () => { console.log('onCheckIn'); },
    onDiscardCheckOut: () => { console.log('onCheckIn'); }
  };

  constructor(props: IAnalyticsLifeCycleProps) {
    super(props);
  }

  public render(): JSX.Element {
    const { lifeCycleStatus, loginUserEmail, userEmail } = this.props;
    let content, link, action, secondaryAction, actionName, secondaryActionName;
    let signal: JSX.Element;

    switch (lifeCycleStatus.status) {
      case AnalyticsLifeCycleStatus.MalwareDetected:
        content = strings.malwareDetected;
        link = LEARN_MORE_LINK;
        actionName = strings.learnMore;
        signal = <MalwareDetectedSignal />;
        break;

      case AnalyticsLifeCycleStatus.ThisFileIsCheckedOut:
        const email = lifeCycleStatus.userCheckedOutThis && lifeCycleStatus.userCheckedOutThis.email;
        const displayName = lifeCycleStatus.userCheckedOutThis && lifeCycleStatus.userCheckedOutThis.displayName;
        if (isCurrentLoginUser(email, loginUserEmail, userEmail)) {
          content = strings.youHaveThisCheckedOut;
          action = this.props.onCheckIn && this._onCheckInClick;
          secondaryAction = this.props.onDiscardCheckOut && this._onDiscardCheckOutClick;
          actionName = strings.checkIn;
          secondaryActionName = strings.discardCheckOut;
          signal = <YouCheckedOutSignal />;
        } else {
          content = StringHelper.format(strings.someoneElseHasThisFileCheckedOut, displayName);
          signal = <SomeoneCheckedOutSignal />;
        }
        break;

      case AnalyticsLifeCycleStatus.DLPBlocked:
        content = strings.DLPBlocked;
        action = this.props.onViewPolicyTip;
        actionName = strings.viewPolicyTip;
        signal = <BlockedSignal />;
        break;

      case AnalyticsLifeCycleStatus.MissingMetaData:
        content = strings.missingMetaData;
        action = this.props.onAddMetadataNow;
        actionName = strings.addMetaData;
        signal = <MissingMetadataSignal />;
        break;

      case AnalyticsLifeCycleStatus.DLPWarning:
        content = strings.DLPWarning;
        action = this.props.onViewPolicyTip;
        actionName = strings.viewPolicyTip;
        signal = <WarningSignal />;
        break;

      case AnalyticsLifeCycleStatus.AwaitingApproval:
        content = strings.awaitingApproval;
        signal = <AwaitingApprovalSignal />;
        break;

      case AnalyticsLifeCycleStatus.Trending:
        content = strings.trendingItem;
        signal = <TrendingSignal />;
        break;

      case AnalyticsLifeCycleStatus.New:
        content = StringHelper.format(
          strings.new, lifeCycleStatus.createdRelativeTime,
          lifeCycleStatus.creator && lifeCycleStatus.creator.displayName
        );
        signal = <div className={ `${AnalyticsLifeCycleStyle.msFileHoverCardLifeCycleNewSignal}` }><NewSignal /></div>;
        break;
      default:
        return <div />;
    }

    return (
      <div className={ `${AnalyticsLifeCycleStyle.msFileHoverCardLifeCycle}` }>
        { signal }
        <div className={ `${AnalyticsLifeCycleStyle.msFileHoverCardLifeCycleMessage}` }>
          <div className='Title'>{ content }</div>
          { (action || link) &&
            <Link
              className={ `${AnalyticsLifeCycleStyle.msFileHoverCardLifeCycleAction}` }
              href={ link }
              target={ link && '_blank' }
              onClick={ action }
            >
              { actionName }
            </Link>
          }
          {
            secondaryAction && ' or '
          }
          {
            secondaryAction &&
            <Link
              className={ `${AnalyticsLifeCycleStyle.msFileHoverCardLifeCycleAction} secondaryAction` }
              onClick={ secondaryAction }
            >
              { secondaryActionName }
            </Link>
          }
        </div>
      </div>
    );

  }

  @autobind
  private _onCheckInClick(ev: React.MouseEvent<HTMLElement>): void {
    this.props.onCheckIn && this.props.onCheckIn();
    // File status changed, clean cache so next hover will show updated status
    this.props.cleanCachedItem && this.props.cleanCachedItem();
  }

  @autobind
  private _onDiscardCheckOutClick(ev: React.MouseEvent<HTMLElement>): void {
    this.props.onDiscardCheckOut && this.props.onDiscardCheckOut();
    // File status changed, clean cache so next hover will show updated status
    this.props.cleanCachedItem && this.props.cleanCachedItem();
  }
}